import React, { useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useFacility } from '../context/FacilityContext';
import DashboardLayout from '../components/DashboardLayout';
import { WeatherForecastStrip } from '../components/WeatherWidget';
import { useIsMobile } from '../hooks/useIsMobile';
import { useAppRefresh } from '../hooks/useAppRefresh';
import { useHomeDashboard } from '../features/home/useHomeDashboard';
import HomeHeader from '../features/home/HomeHeader';
import HomeMobile from '../features/home/HomeMobile';
import QuickActions from '../features/home/QuickActions';
import RainImpactAlert from '../features/home/RainImpactAlert';
import SetCredentialsBanner from '../features/home/SetCredentialsBanner';
import TrialBanner from '../features/home/TrialBanner';
import ModuleGrid from '../features/home/ModuleGrid';
import DashboardStats from '../features/home/DashboardStats';
import HomeDashboardContent from '../features/home/HomeDashboardContent';

const Home = () => {
  const { facilityStatus } = useFacility();
  const dashboard = useHomeDashboard();
  const isMobile = useIsMobile();
  const { refreshApp, refreshing: appRefreshing } = useAppRefresh();

  const handleRefresh = useCallback(async () => {
    await refreshApp();
    await dashboard.refresh();
  }, [refreshApp, dashboard.refresh]);

  const refreshBusy = appRefreshing || dashboard.summaryLoading;

  // A facility that hasn't finished setup belongs in the onboarding wizard.
  // Redirect here (state-driven) so it works regardless of which post-login
  // redirect path ran — the login-time redirect alone proved unreliable on
  // the production/native build.
  if (facilityStatus === 'setup_incomplete') {
    return <Navigate to="/dashboard/onboarding" replace />;
  }

  if (isMobile) {
    return (
      <DashboardLayout>
        <HomeMobile
          {...dashboard}
          onRefresh={handleRefresh}
          refreshBusy={refreshBusy}
        />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-4 md:space-y-6">
        <HomeHeader
          facility={dashboard.facility}
          today={dashboard.today}
          lastSyncedAt={dashboard.lastSyncedAt}
          onRefresh={handleRefresh}
          summaryLoading={refreshBusy}
          summaryError={dashboard.summaryError}
        />

        <TrialBanner />

        <SetCredentialsBanner />

        <QuickActions pendingRequestCount={dashboard.pendingRequestCount} />

        <RainImpactAlert bookingCountByDate={dashboard.bookingCountByDate} />

        <WeatherForecastStrip />

        <DashboardStats
          occupancyPercentage={dashboard.occupancyPercentage}
          bookedSlotsToday={dashboard.bookedSlotsToday}
          totalSlotsToday={dashboard.totalSlotsToday}
          bookingsThisWeek={dashboard.bookingsThisWeek}
          bookingsLastWeek={dashboard.bookingsLastWeek}
          revenueThisMonth={dashboard.revenueThisMonth}
          revenueLastMonth={dashboard.revenueLastMonth}
          revenueDelta={dashboard.revenueDelta}
          pendingRequestCount={dashboard.pendingRequestCount}
          summaryLoading={dashboard.summaryLoading}
          paymentsSummary={dashboard.paymentsSummary}
          overdue={dashboard.overdue}
          totalOpsTasks={dashboard.totalOpsTasks}
          completedOpsTasks={dashboard.completedOpsTasks}
          openTicketsCount={dashboard.openTicketsCount}
        />

        <HomeDashboardContent
          {...dashboard}
          moduleGrid={<ModuleGrid badges={{ pendingRequestCount: dashboard.pendingRequestCount }} />}
        />
      </div>
    </DashboardLayout>
  );
};

export default Home;
